import { createRouter, createWebHistory } from 'vue-router'
import IntroView from '../views/IntroView.vue'
import HomeView from '../views/HomeView.vue'
import About from '../views/AboutView.vue'
import DashboardView from '@/views/DashboardView.vue'
import CandidateDashboardView from '@/views/CandidateDashboardView.vue'
import AssessmentsView from '@/views/AssessmentsView.vue'
import CandidatesView from '@/views/CandidatesView.vue'
import DispatchedView from '@/views/DispatchedView.vue'
import SettingsView from '@/views/SettingsView.vue'
import GetUnlimitedView from '@/views/GetUnlimitedView.vue'
import CandidateAssessmentView from '@/views/CandidateAssessmentView.vue'
import ThanksForSubscribing from '@/views/Unlimited/ThanksForSubscribing.vue'
import InviteView from '@/views/InviteView.vue'
import SignupView from '@/views/SignupView.vue'
import { getCookie, deleteAllCookies } from '@/utils/cookies/cookieUtils.js'
import { endpoint } from '@/utils/endpoint/endpointUtils.js'
import { adminRecruiterRole, adminRole } from '@/utils/roles/roles.js'

const routes = [
  {
    path: '/',
    name: 'home',
    component: IntroView,
    meta: {
      title: 'SkillSynchrony'
    }
  },
  {
    path: '/login',
    name: 'login',
    component: HomeView,
    meta: {
      title: 'SkillSynchrony - Login'
    }
  },
  {
    path: '/signup',
    name: 'signup',
    component: SignupView,
    meta: {
      title: 'SkillSynchrony - Signup'
    }
  },
  {
    path: '/:catchAll(.*)',
    component: HomeView
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: DashboardView,
    meta: { 
      requiresAuth: true,
      title: 'SkillSynchrony - Dashboard'
    }
  },
  {
    path: '/candidate-dashboard',
    name: 'candidate-dashboard',
    component: CandidateDashboardView,
    meta: { 
      requiresAuth: true,
      title: 'SkillSynchrony - Candidate Dashboard'
    }
  },
  {
    path: '/assessments',
    name: 'assessments',
    component: AssessmentsView,
    meta: { 
      requiresAuth: true,
      title: 'SkillSynchrony - Assessments'
    }
  },
  {
    path: '/candidates',
    name: 'candidates',
    component: CandidatesView,
    meta: { 
      requiresAuth: true,
      title: 'SkillSynchrony - Completed'
    }
  },
  {
    path: '/dispatched',
    name: 'dispatched',
    component: DispatchedView,
    meta: { 
      requiresAuth: true,
      title: 'SkillSynchrony - Dispatched'
     }
  },
  {
    path: '/settings',
    name: 'settings',
    component: SettingsView,
    meta: { 
      requiresAuth: true,
      title: 'SkillSynchrony - Settings'
     }
  },
  {
    path: '/get-unlimited',
    name: 'getUnlimited',
    component: GetUnlimitedView,
    meta: { 
      requiresAuth: true,
      title: 'SkillSynchrony - Get Unlimited'
     }
  },
  {
    path: '/candidate-assessment',
    name: "assessment",
    component: CandidateAssessmentView,
    meta: {
      title: 'SkillSynchrony - Start an Assessment'
    }
  },
  {
    path: '/unlimited-purchase-thanks',
    name: 'unlimitedpurchasethanks',
    component: ThanksForSubscribing,
    meta: {
      title: 'SkillSynchrony - Thanks!'
    }
  },
  {
    path: '/invite',
    name: 'invite',
    component: InviteView,
    meta: {
      requiresAdmin: true,
      title: 'SkillSynchrony - Invite'
    }
  },
  {
    path: '/about',
    name: 'about',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    //component: () => import(/* webpackChunkName: "about" */ '../views/AboutView.vue')
    component: About
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

router.beforeEach(async (to, from, next) => {
  // Get token
  const token = getCookie('token') || null;
  const userid = getCookie('userid') || null;
  let isAdminRecruiter = false;
  let isAdmin = false;

  // Routes requiring admin
  if (to.meta.requiresAdmin) {
    console.log('this requires admin');
    if (!token) {
      return next('/dashboard');
    }

    try {
      isAdmin = await adminRole(userid, token, endpoint);
    } catch (error) {
      console.error('Error verifying admin role:', error);
      // Handle error
      return next('/dashboard');
    }

    if (!isAdmin) {
      return next('/dashboard');
    }
  }

  // Routes requiring requiresAuth
  if (to.meta.requiresAuth) {
    console.log('this requires auth');
    // Check if authentication is required for the route
    if (!token) {
      // Redirect to root route if token is missing
      return next('/login');
    }

    try {
      isAdminRecruiter = await adminRecruiterRole(userid, token, endpoint);
    } catch (error) {
      console.error('Error verifying admin/recruiter role:', error);
      // Handle error
      return next('/');
    }

    if (!isAdminRecruiter) {
      deleteAllCookies();
      return next('/login');
    }
  }

  if (to.name === 'home') {
    // Redirect to appropriate dashboard based on user role
    if (token) {
      try {
        isAdminRecruiter = await adminRecruiterRole(userid, token, endpoint);
      } catch (error) {
        console.error('Error verifying admin role:', error);
        // Handle error
        return next('/login');
      }
      
      if (isAdminRecruiter) {
        return next({ name: 'dashboard' });
      } else {
        return next('/candidate-dashboard');
      }
    }
  }

  // Allow navigation to proceed for other routes
  next();
  changeTitles(to, from);
});

function changeTitles(to, from) {
  // This goes through the matched routes from last to first, finding the closest route with a title.
  // e.g., if we have `/some/deep/nested/route` and `/some`, `/deep`, and `/nested` have titles,
  // `/nested`'s will be chosen.
  const nearestWithTitle = to.matched.slice().reverse().find(r => r.meta && r.meta.title);

  // Find the nearest route element with meta tags.
  const nearestWithMeta = to.matched.slice().reverse().find(r => r.meta && r.meta.metaTags);

  const previousNearestWithMeta = from.matched.slice().reverse().find(r => r.meta && r.meta.metaTags);

  // If a route with a title was found, set the document (page) title to that value.
  if(nearestWithTitle) {
    document.title = nearestWithTitle.meta.title;
  } else if(previousNearestWithMeta) {
    document.title = previousNearestWithMeta.meta.title;
  }

  // Remove any stale meta tags from the document using the key attribute we set below.
  Array.from(document.querySelectorAll('[data-vue-router-controlled]')).map(el => el.parentNode.removeChild(el));

  // Skip rendering meta tags if there are none.
 // if(!nearestWithMeta) return next();

  // Turn the meta tag definitions into actual elements in the head.
  nearestWithMeta.meta.metaTags.map(tagDef => {
    const tag = document.createElement('meta');

    Object.keys(tagDef).forEach(key => {
      tag.setAttribute(key, tagDef[key]);
    });

    // We use this to track which meta tags we create so we don't interfere with other ones.
    tag.setAttribute('data-vue-router-controlled', '');

    return tag;
  })
  // Add the meta tags to the document head.
  .forEach(tag => document.head.appendChild(tag));
}

export default router
